/**
 * Carbon number input: a native type="number" field with Carbon's trailing
 * decrease and increase buttons in place of the browser spinners. `min`,
 * `max` and `step` pass through and the buttons honour them (they call the
 * input's own stepDown/stepUp, then fire a native input event so a controlled
 * onChange sees the new value). The buttons stay out of the tab order, as in
 * Carbon: the arrow keys step the focused field. `step="any"` has no step to
 * take, so it shows no buttons.
 */
import { forwardRef, useId, useRef, type ForwardedRef } from 'react'
import clsx from 'clsx'
import { FormField } from './FormField'
import { FIELD_BASE, FIELD_HEIGHT, FIELD_INVALID, fieldIds } from './fieldStyles'
import { IconAdd, IconSubtract } from './icons'
import type { TextInputProps } from './TextInput'

export type NumberInputProps = Omit<TextInputProps, 'type'>

function assignRef<T>(ref: ForwardedRef<T>, value: T | null) {
  if (typeof ref === 'function') ref(value)
  else if (ref) ref.current = value
}

const STEPPER =
  'grid w-10 place-items-center text-text-primary transition-colors duration-110 ease-carbon hover:bg-layer-hover ' +
  'disabled:cursor-not-allowed disabled:bg-transparent disabled:text-text-placeholder'

export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(function NumberInput(
  { label, helper, error, hideLabel, size = 'md', id: idProp, className, wrapperClassName, disabled, readOnly, step, ...rest },
  ref,
) {
  const autoId = useId()
  const id = idProp ?? autoId
  const { describedBy } = fieldIds(id, helper, error)
  const input = useRef<HTMLInputElement | null>(null)
  const steppers = step !== 'any'

  const take = (direction: 1 | -1) => {
    const el = input.current
    if (!el || el.disabled || el.readOnly) return
    if (direction > 0) el.stepUp()
    else el.stepDown()
    el.dispatchEvent(new Event('input', { bubbles: true }))
  }

  return (
    <FormField id={id} label={label} helper={helper} error={error} hideLabel={hideLabel} disabled={disabled} className={wrapperClassName}>
      <div className={clsx('relative', FIELD_HEIGHT[size])}>
        <input
          ref={(el) => {
            input.current = el
            assignRef(ref, el)
          }}
          id={id}
          type="number"
          inputMode="decimal"
          step={step}
          disabled={disabled}
          readOnly={readOnly}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          className={clsx(
            FIELD_BASE,
            'h-full tabular-nums [appearance:textfield]',
            '[&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
            steppers && 'pr-[88px]',
            error && FIELD_INVALID,
            className,
          )}
          {...rest}
        />
        {steppers ? (
          <div className="absolute bottom-px right-0 top-0 flex items-stretch">
            <button type="button" tabIndex={-1} aria-label="Decrease value" disabled={disabled || readOnly} onClick={() => take(-1)} className={STEPPER}>
              <IconSubtract />
            </button>
            <span aria-hidden="true" className="my-2 w-px bg-line-subtle" />
            <button type="button" tabIndex={-1} aria-label="Increase value" disabled={disabled || readOnly} onClick={() => take(1)} className={STEPPER}>
              <IconAdd />
            </button>
          </div>
        ) : null}
      </div>
    </FormField>
  )
})

export default NumberInput
